import { useState } from "react";
import { X, CheckCircle, ArrowRight, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/services/api";

export default function CloseMatchModal({ match, onClose, onMatchClosed }) {
  const players = match.players || [];
  const initialTeamA = match.team_a || [];
  const initialTeamB = match.team_b || [];
  // Scenario A: organizer used "Equilibrer" → flag is_balanced=true, skip manual team composition
  // Scenario B: no balance → flag missing/false, show manual team selection before score
  const teamsAlreadyBalanced = (match.is_balanced || match.teams_balanced) && initialTeamA.length > 0 && initialTeamB.length > 0;
  const [step, setStep] = useState(teamsAlreadyBalanced ? 2 : 1); // 1=teams, 2=score+goals
  const [teamA, setTeamA] = useState(initialTeamA);
  const [teamB, setTeamB] = useState(initialTeamB);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [playerGoals, setPlayerGoals] = useState(
    Object.fromEntries(players.map(p => [p.id, 0]))
  );
  const [noShowIds, setNoShowIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleNoShow = (playerId) => {
    setNoShowIds(prev => prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]);
  };

  const unassigned = players.filter(p => !teamA.includes(p.id) && !teamB.includes(p.id));

  const toggleTeam = (playerId, team) => {
    if (team === 'a') {
      if (teamA.includes(playerId)) {
        setTeamA(prev => prev.filter(id => id !== playerId));
      } else {
        setTeamB(prev => prev.filter(id => id !== playerId));
        setTeamA(prev => [...prev, playerId]);
      }
    } else {
      if (teamB.includes(playerId)) {
        setTeamB(prev => prev.filter(id => id !== playerId));
      } else {
        setTeamA(prev => prev.filter(id => id !== playerId));
        setTeamB(prev => [...prev, playerId]);
      }
    }
  };

  const handleGoalChange = (playerId, value) => {
    const num = Math.max(0, parseInt(value) || 0);
    setPlayerGoals(prev => ({ ...prev, [playerId]: num }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const result = await api.closeMatch(match.id, {
        score_a: scoreA,
        score_b: scoreB,
        player_goals: playerGoals,
        team_a: teamA,
        team_b: teamB,
        no_show_ids: noShowIds,
      });
      onMatchClosed(result);
      toast.success("Match termine! Votez et notez les adversaires.");
    } catch (error) {
      toast.error(error.message || "Erreur");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPlayerName = (id) => players.find(p => p.id === id)?.name || "?";
  const allAssigned = teamA.length > 0 && teamB.length > 0 && unassigned.length === 0;

  return (
    <div
      data-testid="close-match-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.85)', padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: '400px', maxHeight: '85vh',
          backgroundColor: '#111', border: '1px solid #333',
          borderRadius: '20px', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#fff' }}>
            {step === 1 ? 'Composer les equipes' : 'Score & Buts'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>

          {/* STEP 1: Team composition */}
          {step === 1 && (
            <div>
              <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                Glisse chaque joueur dans son equipe. Tu peux modifier si besoin.
              </p>

              {/* Unassigned players */}
              {unassigned.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '8px' }}>Non assignes</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {unassigned.map(p => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: '#0a0a0a', borderRadius: '10px' }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#fff' }}>
                          {p.name?.charAt(0)}
                        </div>
                        <span style={{ flex: 1, fontSize: '13px', color: '#fff' }}>{p.name}</span>
                        <button onClick={() => toggleTeam(p.id, 'a')} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, border: 'none', backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e', cursor: 'pointer' }}>
                          Eq.A
                        </button>
                        <button onClick={() => toggleTeam(p.id, 'b')} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, border: 'none', backgroundColor: 'rgba(59,130,246,0.15)', color: '#60a5fa', cursor: 'pointer' }}>
                          Eq.B
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Team A */}
              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#22c55e', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Equipe A ({teamA.length})
                </p>
                <div style={{ minHeight: '40px', borderRadius: '10px', border: '1px dashed rgba(34,197,94,0.3)', padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {teamA.map(id => (
                    <span key={id} onClick={() => toggleTeam(id, 'a')} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e', cursor: 'pointer' }}>
                      {getPlayerName(id)} ✕
                    </span>
                  ))}
                  {teamA.length === 0 && <span style={{ fontSize: '11px', color: '#6b7280' }}>Aucun joueur</span>}
                </div>
              </div>

              {/* Team B */}
              <div>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#60a5fa', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Equipe B ({teamB.length})
                </p>
                <div style={{ minHeight: '40px', borderRadius: '10px', border: '1px dashed rgba(59,130,246,0.3)', padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {teamB.map(id => (
                    <span key={id} onClick={() => toggleTeam(id, 'b')} style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', backgroundColor: 'rgba(59,130,246,0.15)', color: '#60a5fa', cursor: 'pointer' }}>
                      {getPlayerName(id)} ✕
                    </span>
                  ))}
                  {teamB.length === 0 && <span style={{ fontSize: '11px', color: '#6b7280' }}>Aucun joueur</span>}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Score + Goals */}
          {step === 2 && (
            <div>
              {teamsAlreadyBalanced && (
                <div data-testid="teams-prebalanced-banner" style={{
                  marginBottom: '16px', padding: '12px 14px',
                  backgroundColor: 'rgba(34,197,94,0.08)',
                  border: '1px solid rgba(34,197,94,0.3)',
                  borderRadius: '10px',
                  display: 'flex', alignItems: 'center', gap: '10px',
                }}>
                  <Users size={16} color="#22c55e" />
                  <span style={{ fontSize: '12px', color: '#22c55e', lineHeight: 1.4 }}>
                    Equipes deja equilibrees automatiquement ({teamA.length} vs {teamB.length}). Tu passes directement au score.
                  </span>
                </div>
              )}
              {/* Score Final */}
              <div style={{ marginBottom: '24px' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '12px', fontWeight: 600 }}>
                  Score Final
                </p>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center', gap: '12px',
                  backgroundColor: '#0a0a0a', borderRadius: '12px', padding: '16px',
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '10px', color: '#22c55e', marginBottom: '8px', fontWeight: 600 }}>EQUIPE A</p>
                    <input
                      data-testid="score-a-input"
                      type="number"
                      min="0"
                      value={scoreA}
                      onChange={(e) => setScoreA(Math.max(0, parseInt(e.target.value) || 0))}
                      style={{
                        width: '60px', height: '48px', textAlign: 'center',
                        fontSize: '24px', fontWeight: 700, color: '#fff',
                        backgroundColor: '#1a1a1a', border: '1px solid #333',
                        borderRadius: '10px', outline: 'none',
                      }}
                    />
                  </div>
                  <span style={{ fontSize: '24px', fontWeight: 700, color: '#6b7280' }}>-</span>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '10px', color: '#3b82f6', marginBottom: '8px', fontWeight: 600 }}>EQUIPE B</p>
                    <input
                      data-testid="score-b-input"
                      type="number"
                      min="0"
                      value={scoreB}
                      onChange={(e) => setScoreB(Math.max(0, parseInt(e.target.value) || 0))}
                      style={{
                        width: '60px', height: '48px', textAlign: 'center',
                        fontSize: '24px', fontWeight: 700, color: '#fff',
                        backgroundColor: '#1a1a1a', border: '1px solid #333',
                        borderRadius: '10px', outline: 'none',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Buts par joueur */}
              <div>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '12px', fontWeight: 600 }}>
                  Buts par joueur
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {players.map((player) => {
                    const isNoShow = noShowIds.includes(player.id);
                    return (
                      <div key={player.id} style={{
                        display: 'grid', gridTemplateColumns: '1fr 70px',
                        alignItems: 'center', gap: '10px',
                        backgroundColor: isNoShow ? 'rgba(239,68,68,0.08)' : '#0a0a0a',
                        borderRadius: '10px', padding: '10px 14px',
                        opacity: isNoShow ? 0.55 : 1,
                        transition: 'opacity 0.15s ease',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '28px', height: '28px', borderRadius: '50%',
                            backgroundColor: teamA.includes(player.id) ? 'rgba(34,197,94,0.3)' : 'rgba(59,130,246,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '11px', fontWeight: 700, color: '#fff',
                          }}>
                            {player.name?.charAt(0)}
                          </div>
                          <span style={{ fontSize: '13px', color: '#fff', textDecoration: isNoShow ? 'line-through' : 'none' }}>{player.name}</span>
                        </div>
                        <input
                          type="number" min="0"
                          value={playerGoals[player.id] || 0}
                          onChange={(e) => handleGoalChange(player.id, e.target.value)}
                          disabled={isNoShow}
                          style={{
                            width: '100%', height: '34px', textAlign: 'center',
                            fontSize: '16px', fontWeight: 600, color: '#22c55e',
                            backgroundColor: '#1a1a1a', border: '1px solid #333',
                            borderRadius: '8px', outline: 'none',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* No-show / absences */}
              <div style={{ marginTop: '20px' }}>
                <p style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '4px', fontWeight: 600 }}>
                  Absents (no-show)
                </p>
                <p style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '10px' }}>
                  Coche les joueurs qui ne sont pas venus. Sanction : -25% de fiabilite.
                </p>
                <div data-testid="no-show-list" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {players.map(player => {
                    const checked = noShowIds.includes(player.id);
                    return (
                      <button
                        key={player.id}
                        type="button"
                        data-testid={`no-show-${player.id}`}
                        onClick={() => toggleNoShow(player.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 12px', borderRadius: '10px',
                          backgroundColor: checked ? 'rgba(239,68,68,0.12)' : '#0a0a0a',
                          border: checked ? '1px solid rgba(239,68,68,0.5)' : '1px solid #222',
                          color: checked ? '#f87171' : '#d1d5db',
                          fontSize: '13px', cursor: 'pointer', textAlign: 'left',
                          fontFamily: 'inherit', touchAction: 'manipulation',
                        }}
                      >
                        <span style={{
                          width: '18px', height: '18px', borderRadius: '4px',
                          border: checked ? '2px solid #f87171' : '1.5px solid #444',
                          backgroundColor: checked ? '#f87171' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#000', fontSize: '12px', fontWeight: 800,
                        }}>{checked ? '✕' : ''}</span>
                        <span style={{ flex: 1 }}>{player.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid #222' }}>
          {step === 1 ? (
            <button
              data-testid="next-step-btn"
              onClick={() => setStep(2)}
              disabled={!allAssigned}
              style={{
                width: '100%', height: '48px', borderRadius: '12px',
                backgroundColor: allAssigned ? '#22c55e' : '#333', border: 'none',
                color: allAssigned ? '#fff' : '#6b7280', fontSize: '14px', fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                cursor: allAssigned ? 'pointer' : 'not-allowed',
              }}
            >
              <Users size={18} /> Suivant: Score & Buts <ArrowRight size={16} />
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '10px' }}>
              {!teamsAlreadyBalanced && (
                <button
                  onClick={() => setStep(1)}
                  style={{ flex: '0 0 auto', height: '48px', padding: '0 16px', borderRadius: '12px', backgroundColor: 'transparent', border: '1px solid #333', color: '#9ca3af', fontSize: '13px', cursor: 'pointer' }}
                >
                  Retour
                </button>
              )}
              <button
                data-testid="confirm-close-match"
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{
                  flex: 1, height: '48px', borderRadius: '12px',
                  backgroundColor: '#22c55e', border: 'none',
                  color: '#fff', fontSize: '14px', fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.6 : 1,
                }}
              >
                <CheckCircle size={18} />
                {isSubmitting ? "Validation..." : "Valider et Terminer"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
